import React, { useEffect, useMemo, useRef, useState } from "react";

import { OrganizationOrder } from "@packages/client";
import { LoadingIcon } from "@packages/ds";
import { Elements, useStripe } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import moment from "moment";
import { useIntl } from "react-intl";
import { useNavigate, useParams } from "react-router-dom";

import { getOrderAuthentication } from "Api/order/authenticate/getOrderAuthentication.request";
import {
  OrderAuthentication,
  OrderAuthenticationError
} from "Api/order/authenticate/OrderAuthentication.types";
import Button, { ButtonWrapper, Link } from "ds/Button";
import { useEventListener } from "Hooks/useEventListener";
import getUrl from "Libs/getUrl";
import { useLoadOrder } from "Reducers/organization/order/hooks/useLoadOrder";
import { organizationByDescriptionIdSelector } from "Reducers/organization/organization";
import {
  organizationPaymentSourceLoadingSelector,
  organizationPaymentSourceSelector
} from "Reducers/organization/paymentSource/paymentSource.selectors";
import { useAppSelector } from "Store/hooks";

import * as S from "./styles";

function useRetrieveSetupIntent({
  clientSecret,
  onFinish
}: {
  clientSecret: string;
  onFinish: (status: "not_needed" | "succeeded" | "failed") => any;
}) {
  const stripe = useStripe();
  const [hasCreatedListener, setHasCreatedListener] = useState(false);
  const isProcessingApiCall = useRef(false);

  function on3DSComplete() {
    if (isProcessingApiCall.current) return;
    isProcessingApiCall.current = true;

    stripe
      ?.retrievePaymentIntent(clientSecret)
      .then(function (result) {
        if (result.paymentIntent?.status === "succeeded") {
          return onFinish("succeeded");
        }

        return onFinish("failed");
      })
      .finally(() => {
        isProcessingApiCall.current = false;
      });
  }

  useEventListener(
    "message",
    ev => {
      if (
        clientSecret &&
        stripe &&
        !hasCreatedListener &&
        ev.data === "3DS-authentication-complete"
      ) {
        setHasCreatedListener(true);
        on3DSComplete();
      }
    },
    undefined,
    undefined,
    [stripe, clientSecret]
  );
}

function useOrderAuthentication(
  options: { orderId?: string; organizationId?: string },
  onFinish: (status: "not_needed" | "succeeded" | "failed") => any
) {
  const { orderId, organizationId } = options;
  const [isLoading, setIsLoading] = useState(false);
  const [orderAuthentication, setOrderAuthentication] =
    useState<OrderAuthentication>();
  const [error, setError] = useState<string | undefined>();

  useEffect(() => {
    async function load() {
      setIsLoading(true);
      try {
        const orderAuthentication = (await getOrderAuthentication({
          orderId: orderId!,
          organizationId: organizationId!
        })) as OrderAuthentication;
        setOrderAuthentication(orderAuthentication);
      } catch (error) {
        const typedError = error as OrderAuthenticationError;
        if (typedError.status === 400) {
          setIsLoading(false);
          onFinish("not_needed");
        }
        setError(typedError.title);
      }
      setIsLoading(false);
    }

    if (
      !isLoading &&
      !orderAuthentication &&
      !error &&
      organizationId &&
      orderId
    ) {
      load();
    }
  }, [
    error,
    isLoading,
    onFinish,
    orderAuthentication,
    orderId,
    organizationId
  ]);

  return {
    isLoading,
    orderAuthentication,
    error
  };
}

type SCAProps = {
  order?: OrganizationOrder;
  isLoadingOrderInformation: boolean;
  isLoadingOrderAuthentication: boolean;
  orderAuthentication: OrderAuthentication;
  orderAuthenticationError?: OrderAuthenticationError;
};

const SCAPage: React.FC<SCAProps> = ({
  order,
  isLoadingOrderInformation,
  isLoadingOrderAuthentication,
  orderAuthentication,
  orderAuthenticationError
}) => {
  const intl = useIntl();
  const { organizationId: organizationDescriptionId, orderId } = useParams<{
    organizationId: string;
    orderId: string;
  }>();
  const [isStripeOpen, setIsStripeOpen] = useState(false);
  const navigation = useNavigate();
  useRetrieveSetupIntent({
    clientSecret: orderAuthentication.intent.client_secret,
    onFinish: status => {
      navigation(
        getUrl({
          key: "organization.billing",
          props: { organizationId: organizationDescriptionId! }
        }) + `?status=${status}&orderId=${order?.id}`
      );
    }
  });
  const paymentSource = useAppSelector(state =>
    organizationPaymentSourceSelector(state, {
      organizationId: organizationDescriptionId!
    })
  );
  const isLoadingPaymentSource = useAppSelector(
    organizationPaymentSourceLoadingSelector
  );

  if (
    isLoadingPaymentSource ||
    isLoadingOrderInformation ||
    isLoadingOrderAuthentication ||
    (!orderAuthentication && !orderAuthenticationError) ||
    !order ||
    !paymentSource
  ) {
    return (
      <S.Wrapper>
        <S.Container>
          <LoadingIcon />
        </S.Container>
      </S.Wrapper>
    );
  }

  return (
    <S.Wrapper>
      <S.Container>
        <S.Title tag="h1">{intl.formatMessage({ id: "sca.title" })}</S.Title>
        <S.Description>
          {intl.formatMessage<React.ReactNode>(
            { id: "sca.description" },
            {
              ComissionLink: children => (
                <Link
                  target="_blank"
                  to={intl.formatMessage({ id: "links.sca.commision" })}
                >
                  {children}
                </Link>
              ),
              LearnMoreLink: children => (
                <Link
                  target="_blank"
                  to={intl.formatMessage({ id: "links.sca.learnMore" })}
                >
                  {children}
                </Link>
              )
            }
          )}
        </S.Description>
        <S.Section>
          <S.SectionTitle tag="h3">
            {intl.formatMessage({ id: "sca.order.title" })}
          </S.SectionTitle>
          <S.SectionRow>
            <S.SectionRowIdentifier weight="semiBold">
              {order.id}
            </S.SectionRowIdentifier>
            <S.SectionRowDate weight="semiBold">
              {moment(order.billing_period_start).format("MMM d, YYYY")}
              {" - "}
              {moment(order.billing_period_end).format("MMM d, YYYY")}
            </S.SectionRowDate>
            <S.SectionRowPrice weight="semiBold">
              {order.total_formatted}
            </S.SectionRowPrice>
            <Button
              variant="link"
              to={getUrl({
                key: "organization.billing.invoices.invoice",
                props: {
                  organizationId: organizationDescriptionId!,
                  orderId: orderId!
                }
              })}
            >
              {intl.formatMessage({ id: "sca.order.cta" })}
            </Button>
          </S.SectionRow>
        </S.Section>
        <S.Section>
          <S.SectionTitle tag="h3">
            {intl.formatMessage({ id: "sca.payment.title" })}
          </S.SectionTitle>
          <S.SectionRow>
            <S.SectionRowIdentifier weight="semiBold">
              {paymentSource.data?.type}
            </S.SectionRowIdentifier>
            <S.SectionRowDate weight="semiBold">
              {paymentSource.number}
            </S.SectionRowDate>
            <S.SectionRowPrice weight="semiBold">
              {intl.formatMessage(
                { id: "sca.payment.expiration" },
                {
                  month: paymentSource.data?.exp_month,
                  year: paymentSource.data?.exp_year
                }
              )}
            </S.SectionRowPrice>
            <Button
              variant="link"
              to={getUrl({
                key: "organization.billing.details",
                props: { organizationId: organizationDescriptionId! }
              })}
            >
              {intl.formatMessage({ id: "sca.payment.cta" })}
            </Button>
          </S.SectionRow>
        </S.Section>
        <ButtonWrapper>
          <Button variant="secondary" to="/">
            {intl.formatMessage({ id: "cancel" })}
          </Button>
          <Button
            onClick={() => {
              setIsStripeOpen(true);
            }}
          >
            {intl.formatMessage({ id: "sca.authenticate" })}
          </Button>
        </ButtonWrapper>
      </S.Container>
      {isStripeOpen && (
        <>
          <S.Stripe3DS
            title="stripe-3ds"
            src={orderAuthentication?.redirect_to_url?.url}
            width="500"
            height="600"
          />
          <S.Overlay />
        </>
      )}
    </S.Wrapper>
  );
};

const StripeWrapper = props => {
  const { organizationId: organizationDescriptionId, orderId } = useParams<{
    organizationId: string;
    orderId: string;
  }>();
  const navigation = useNavigate();
  const organization = useAppSelector(state =>
    organizationByDescriptionIdSelector(state, {
      organizationDescriptionId
    })
  );
  const { order, loading: isLoadingOrderInformation } = useLoadOrder({
    organizationId: organizationDescriptionId!,
    orderId
  });
  const {
    isLoading: isLoadingOrderAuthentication,
    orderAuthentication,
    error: orderAuthenticationError
  } = useOrderAuthentication(
    { orderId: order?.id, organizationId: organization?.id },
    status => {
      navigation(
        getUrl({
          key: "organization.billing",
          props: { organizationId: organizationDescriptionId! }
        }) + `?status=${status}`
      );
    }
  );

  const stripePromise = useMemo(() => {
    if (orderAuthentication?.intent.public_key)
      return loadStripe(orderAuthentication?.intent.public_key);
  }, [orderAuthentication?.intent.public_key]);

  if (!stripePromise || !orderAuthentication)
    return (
      <S.Wrapper>
        <S.Container>
          <LoadingIcon />
        </S.Container>
      </S.Wrapper>
    );

  return (
    <Elements stripe={stripePromise}>
      <SCAPage
        {...props}
        order={order}
        isLoadingOrderInformation={isLoadingOrderInformation}
        isLoadingOrderAuthentication={isLoadingOrderAuthentication}
        orderAuthentication={orderAuthentication}
        orderAuthenticationError={orderAuthenticationError}
      />
    </Elements>
  );
};

export default StripeWrapper;
